import moment from 'moment/moment';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect, useState, useRef } from 'react';
import {
  fetchPackQualityOfTimeSpent,
  setPage,
  fetchPackSubjectWiseTime,
  fetchPackHourWiseAnalysis,
} from '../../../../../apps/frontend/src/redux/slices/packSlice';
import SubjectTabs from '../generic/SubjectTabs';
import TestFilterTabs from '../generic/TestFilterTabs';
import TimeAnalysisSummary from './TimeAnalysisSummary';
import TimeAnalysisTestBreakdown from './TimeAnalysisTestBreakdown';
import TimeAnalysisSubjectChart from './TimeAnalysisSubjectChart';
import TimeAnalysisSubjectTestBreakdown from './TimeAnalysisSubjectTestBreakdown';
import TimeAnalysisHourBreakdown from './TimeAnalysisHourBreakdown';
import { useNavigate } from 'react-router-dom';
import PackPagination from '../generic/PackPagination';
import { ArrowLeft } from '@phosphor-icons/react';
import PackButton from '../generic/PackButton';

const TimeAnalysis = ({ pack, packId, onResetFilters }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isDark } = useSelector((state) => state.user);
  const {
    qualityOfTimeSpent: { data: qualityData, loading: qualityLoading },
    subjectWiseTime: { data: subjectData, loading: subjectLoading },
    hourWiseBreakDown: { data: hourData, loading: hourLoading },
    filters,
  } = useSelector((state) => state.pack);

  const [headers, setHeaders] = useState([]);

  // Scroll states for hide-on-scroll behavior
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isDescriptionVisible, setIsDescriptionVisible] = useState(true);
  const scrollContainerRef = useRef(null);

  // ✅ Refetch when page OR filter changes
  useEffect(() => {
    if (!packId) return;
    dispatch(fetchPackQualityOfTimeSpent({ packId, page: filters?.page, testFilter: filters?.testFilter }));
    dispatch(fetchPackSubjectWiseTime({ packId, page: filters?.page, testFilter: filters?.testFilter }));
    dispatch(fetchPackHourWiseAnalysis({ packId, page: filters?.page, testFilter: filters?.testFilter }));
  }, [dispatch, packId, filters?.page, filters?.testFilter]);

  useEffect(() => {
    setHeaders(pack?.subjects);
  }, [pack]);

  // Scroll handler for hide-on-scroll behavior
  useEffect(() => {
    const handleScroll = () => {
      if (!scrollContainerRef.current) return;

      const currentScrollY = scrollContainerRef.current.scrollTop;
      const isTabletOrBelow = window.innerWidth < 1024; // lg breakpoint

      if (!isTabletOrBelow) {
        if (currentScrollY <= 0) {
          if (!isDescriptionVisible) setIsDescriptionVisible(true);
        } else if (currentScrollY > 70 && isDescriptionVisible) {
          setIsDescriptionVisible(false);
        } else if (currentScrollY < 30 && !isDescriptionVisible) {
          setIsDescriptionVisible(true);
        }
        setIsHeaderVisible(true);
        return;
      }

      // Mobile/Tablet
      if (currentScrollY < lastScrollY) {
        setIsHeaderVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderVisible(false);
      }

      setLastScrollY(currentScrollY);
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [lastScrollY]);

  // Total pages from API (fallback 1)
  const totalPages = qualityData?.totalPagesAvailable || 1;
  const activePage = filters?.page || 1;

  return (
    <div className="w-full max-w-4xl mx-auto flex flex-col h-screen overflow-y-auto" ref={scrollContainerRef}>
      {/* Sticky Header */}
      <div
        className={`z-10 q3-glass rounded transition-transform duration-300 ease-in-out lg:translate-y-0 ${
          isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        <div className="p-6 pb-4">
          {/* Mobile Back Button */}
          <PackButton navigateTo="/v2/home" />

          <div className="mb-6">
            <h1 className="text-3xl font-bold text-[var(--q3-neutral-default)]">Time Analysis</h1>

            {/* <Breadcrumb items={breadcrumbItems} /> */}
          </div>

          <TestFilterTabs />
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="p-6 pt-2 space-y-8">
        <TimeAnalysisSummary
          data={qualityData?.summary}
          loading={qualityLoading}
          isDark={isDark}
          onResetFilters={onResetFilters}
        />

        <TimeAnalysisTestBreakdown
          data={qualityData?.testWiseQoTs}
          loading={qualityLoading}
          headers={headers}
          isDark={isDark}
          onResetFilters={onResetFilters}
        />

        <TimeAnalysisSubjectChart
          data={subjectData?.summary}
          loading={subjectLoading}
          onResetFilters={onResetFilters}
        />

        <TimeAnalysisSubjectTestBreakdown
          data={subjectData?.TestAnalysis}
          loading={subjectLoading}
          summary={subjectData?.summary}
          onResetFilters={onResetFilters}
        />

        <TimeAnalysisHourBreakdown
          data={hourData?.hourWiseBreakDowm}
          loading={hourLoading}
          onResetFilters={onResetFilters}
        />

        {/* ✅ Pagination only at bottom */}
        {totalPages > 1 && (
          <PackPagination
            totalPages={totalPages}
            activePage={activePage}
            onPageClick={(page) => dispatch(setPage(page))}
          />
        )}
      </div>
    </div>
  );
};

export default TimeAnalysis;
